import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChatBubbleIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import GroupIcon from "@mui/icons-material/Group";

import { setSelectedChat, createChat } from "../../../redux/chats/actions";
import { updateNotificationStatus } from "../../../redux/notifications/actions";
import { useStyles } from "./ChatListStyles";
import ListElement from "../ListElement";
import SoftInput from "../../SoftInput";
import SoftBox from "../../SoftBox";
import SoftButton from "../../SoftButton";
import SoftTypography from "../../SoftTypography";
import SearchedUserList from "../SearchedUserList/index";
import { UseUsersDictionary, UseCompanyColors } from "../../../hooks/company";
import { UseUserDetails } from "../../../hooks/auth";
import GroupChatModal from "../GroupChatModal";
import { getReceiverImage, getReceiverName } from "../helpers";
import LoadingScreen from "../../LoadingScreen";
import blankPic from "../../../img/blankPic.png";
import { DEFAULT_BRAND, brandDarken } from "../../../helpers/brandColor";
import moment from "moment";

const ChatList = ({ chats, onSelected, selected, fetchingChats }) => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [users, setUsers] = useState([]);
  const [searchedUser, setSearchedUser] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const searchInputRef = useRef(null);

  const { primaryColor: companyBrandColor } = UseCompanyColors();
  const brand = companyBrandColor || DEFAULT_BRAND;
  const brandDark = brandDarken(brand, 0.18);

  const usersDict = UseUsersDictionary();
  const userDetails = UseUserDetails();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { unreadNotifications } = useSelector(
    (state) => state.notificationsReducer,
  );

  const getUnreadCountForChat = (chatId) =>
    unreadNotifications.filter(
      (noti) => noti.action === "chat" && noti.chat === chatId,
    ).length;

  const markChatNotificationsRead = (chatId) => {
    unreadNotifications
      .filter((noti) => noti.action === "chat" && noti.chat === chatId && noti.notiId)
      .forEach((noti) => dispatch(updateNotificationStatus(noti.notiId)));
  };

  useEffect(() => {
    const values = Object.keys(usersDict).map((key) => ({
      _id: key,
      value: key,
      label: `${usersDict[key].firstName} ${usersDict[key].lastName}`,
      ...usersDict[key],
    }));

    const filteredUsers = values.filter((item) => userDetails._id !== item._id);
    setUsers(filteredUsers);
  }, []);

  const onChangeTrigger = async (e) => {
    setSearchKeyword(e.target.value);

    const filterList = users.filter((item) => {
      const userName = `${item.firstName} ${item.lastName}`;
      return userName.toLowerCase().search(e.target.value.toLowerCase()) !== -1;
    });

    setSearchedUser(filterList);
  };

  const createChatTrigger = async (userId) => {
    try {
      const payload = {
        userId,
      };
      dispatch(createChat(payload));
      setSearchKeyword("");
    } catch (err) {
      console.log(err.response);
    }
  };

  return (
    <>
      {openModal && (
        <GroupChatModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          users={users}
        />
      )}
      <div className={classes.chatListContainer}>
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <SoftTypography
            display="block"
            variant="h6"
            sx={{
              fontSize: "16px",
            }}
          >
            Players Chat
          </SoftTypography>
          <SoftButton
            size="small"
            variant="outlined"
            color="primary"
            iconOnly
            aria-label="Create group chat"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setOpenModal(true)}
          >
            <GroupIcon fontSize="small" />
          </SoftButton>
        </SoftBox>
        <SoftInput
          ref={searchInputRef}
          placeholder="Search Players"
          value={searchKeyword}
          onChange={onChangeTrigger}
          sx={{ borderRadius: "12px" }}
        />
        {/* eslint-disable */}
        <div className={classes.chatList}>
          {searchKeyword === "" ? (
            fetchingChats ? (
              <LoadingScreen height="200px" logoHeight="200px" />
            ) : chats.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center gap-2 py-10 px-4">
                <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-1">
                  <ChatBubbleIcon sx={{ fontSize: 28, color: "#2563EB" }} />
                </div>
                <SoftTypography variant="h6" sx={{ fontSize: "15px", fontWeight: 700 }}>
                  Start a conversation
                </SoftTypography>
                <SoftTypography
                  variant="button"
                  sx={{ fontSize: "12px", color: "#94A3B8", fontWeight: 400, maxWidth: "220px" }}
                >
                  Message a teammate or start a group chat for your crew.
                </SoftTypography>
                <SoftButton
                  size="small"
                  sx={{
                    mt: 1,
                    borderRadius: "9999px !important",
                    textTransform: "capitalize",
                    fontSize: "12px",
                    color: "#FFFFFF !important",
                    background: `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%) !important`,
                  }}
                  onClick={() => searchInputRef.current?.focus()}
                >
                  + New message
                </SoftButton>
              </div>
            ) : (
              chats.map((item, index) => (
                <ListElement
                  key={item._id}
                  selected={selected === index ? true : false} // eslint-disable-line
                  id={index}
                  name={
                    item.isGroupChat
                      ? item.chatName
                      : getReceiverName(item.users, userDetails)
                  }
                  latestMessage={item?.latestMessage?.message}
                  image={
                    getReceiverImage(item.users, userDetails) === null
                      ? null
                      : getReceiverImage(item.users, userDetails)
                  }
                  isGroupChat={item?.isGroupChat}
                  unreadCount={getUnreadCountForChat(item._id)}
                  onSelected={() => onSelected(index)}
                  setSelectedChat={() => {
                    markChatNotificationsRead(item._id);
                    dispatch(setSelectedChat(item));
                  }}
                  messageTime={moment(item?.latestMessage?.createdAt).fromNow()}
                />
              ))
            )
          ) : (
            searchedUser.map((item) => (
              <SearchedUserList
                key={item._id}
                image={item.image}
                name={`${item.firstName} ${item.lastName}`}
                userId={item._id}
                createChatTrigger={createChatTrigger}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ChatList;
